// src/pages/booking/ResourceDetailPage.tsx
// Resource detail: overview + book, reviews, waitlist (owner), insights (owner), manage (owner).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import type { SelectOption } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { useScope } from '@/hooks/useScope';
import { authStore } from '@core/stores';
import { BookDialog } from '@/features/booking/BookDialog';
import { ResourceFormDialog } from '@/features/booking/ResourceFormDialog';
import {
  addLocalWaitlistEntry,
  errMessage,
  fmtMoney,
  RESOURCE_STATUS_KEYS,
  RESOURCE_STATUS_VARIANT,
  WAITLIST_STATUS_KEYS,
} from '@/features/booking/lib';
import { GetResource } from '@core/api/endpoints/booking/get_resource';
import { DeleteResource } from '@core/api/endpoints/booking/delete_resource';
import { SetResourceStatus } from '@core/api/endpoints/booking/set_resource_status';
import { JoinWaitlist } from '@core/api/endpoints/booking/join_waitlist';
import { ListWaitlistEntries } from '@core/api/endpoints/booking/list_waitlist_entries';
import { ListResourceReviews } from '@core/api/endpoints/booking/list_resource_reviews';
import { RespondToReview } from '@core/api/endpoints/booking/respond_to_review';
import { GetResourceStats } from '@core/api/endpoints/booking/get_resource_stats';
import { ListBookingTypes } from '@core/api/endpoints/booking/list_booking_types';
import type {
  BookableResource,
  BookingReview,
  BookingTypeDefinition,
  ReviewSummary,
  ResourceStatsResponse,
  WaitlistEntry,
  BookableResourceStatus,
} from '@core/types/booking';

type Tab = 'overview' | 'reviews' | 'waitlist' | 'insights';

export const ResourceDetailPage = observer(() => {
  const { t, locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const scope = useScope();

  const [resource, setResource] = useState<BookableResource | null>(null);
  const [bookingTypes, setBookingTypes] = useState<BookingTypeDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  const [bookOpen, setBookOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  const [reviews, setReviews] = useState<BookingReview[]>([]);
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const [stats, setStats] = useState<ResourceStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const isOwner = !!resource?.provider?.entity?.id && resource.provider.entity.id === authStore.activeProfileId;

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await GetResource(id);
      setResource(res.resource ?? null);
    } catch (e) {
      setError(errMessage(e, t('booking.resource.loadError')));
    } finally {
      setIsLoading(false);
    }
  }, [id, scope.productId, t]);

  useEffect(() => {
    load();
    ListBookingTypes({ product_id: scope.productId, limit: 100 }).then((r) => setBookingTypes(r.definitions ?? [])).catch(() => {});
  }, [load, scope.productId]);

  useEffect(() => {
    if (tab !== 'reviews' || !id) return;
    setReviewsLoading(true);
    ListResourceReviews(id, { limit: 50 })
      .then((r) => {
        setReviews(r.reviews ?? []);
        setReviewSummary(r.summary ?? null);
      })
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  }, [tab, id, scope.productId]);

  useEffect(() => {
    if (tab !== 'waitlist' || !id || !isOwner) return;
    setWaitlistLoading(true);
    ListWaitlistEntries(id, { limit: 100 })
      .then((r) => setWaitlist(r.entries ?? []))
      .catch(() => {})
      .finally(() => setWaitlistLoading(false));
  }, [tab, id, scope.productId, isOwner]);

  useEffect(() => {
    if (tab !== 'insights' || !id || !isOwner) return;
    setStatsLoading(true);
    GetResourceStats(id)
      .then(setStats)
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, [tab, id, scope.productId, isOwner]);

  const statusOptions: SelectOption[] = Object.keys(RESOURCE_STATUS_KEYS)
    .filter((k) => k !== 'BOOKABLE_RESOURCE_STATUS_UNSPECIFIED')
    .map((k) => ({ value: k, label: t(`booking.resource.status.${RESOURCE_STATUS_KEYS[k]}`) }));

  const changeStatus = async (status: string) => {
    if (!resource?.entity) return;
    try {
      const res = await SetResourceStatus(resource.entity.id, { resource: resource.entity, status: status as BookableResourceStatus });
      setResource(res.resource ?? resource);
    } catch (e) {
      setError(errMessage(e, t('booking.resource.saveError')));
    }
  };

  const doDelete = async () => {
    if (!resource?.entity) return;
    try {
      await DeleteResource(resource.entity.id);
      navigate('/booking');
    } catch (e) {
      setError(errMessage(e, t('booking.resource.deleteError')));
      setDeleteOpen(false);
    }
  };

  const joinWaitlist = async () => {
    if (!resource?.entity) return;
    setJoiningWaitlist(true);
    try {
      const res = await JoinWaitlist(resource.entity.id, {
        resource: resource.entity,
        flexible_time: true,
      });
      if (res.entry?.entity?.id) {
        addLocalWaitlistEntry({
          entryId: res.entry.entity.id,
          resourceId: resource.entity.id,
          resourceName: resource.name,
          productId: scope.productId,
          position: res.entry.position ?? 0,
          status: res.entry.status ?? 'WAITLIST_STATUS_WAITING',
          joinedAt: new Date().toISOString(),
        });
      }
      setWaitlistJoined(res.entry?.position ?? 0);
    } catch (e) {
      setError(errMessage(e, t('booking.waitlist.joinError')));
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const respond = async (review: BookingReview, text: string) => {
    if (!review.entity || !resource?.entity) return;
    try {
      const res = await RespondToReview(review.entity.id, {
        review: review.entity,
        resource_id: resource.entity.id,
        response_text: text,
      });
      setReviews((prev) => prev.map((r) => (r.entity?.id === review.entity?.id ? res.review ?? r : r)));
    } catch (e) {
      setError(errMessage(e, t('booking.review.respondError')));
    }
  };

  if (isLoading && !resource) {
    return (
      <div className="mx-auto max-w-5xl space-y-3 p-6" aria-busy="true">
        <div className="h-8 w-1/3 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  if (error && !resource) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <Alert variant="error">{error}</Alert>
        <Button size="sm" variant="secondary" className="mt-3" onClick={load}>
          {t('booking.retry')}
        </Button>
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <p className="text-sm text-muted-foreground">{t('booking.resource.notFound')}</p>
      </div>
    );
  }

  const status = resource.status ?? 'BOOKABLE_RESOURCE_STATUS_ACTIVE';

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <Link to="/booking" className="text-sm text-primary hover:underline">
        {t('booking.catalog.backToCatalog')}
      </Link>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="overflow-hidden rounded-lg border border-border">
        <div className="aspect-[21/9] w-full bg-muted">
          {resource.cover_image_url ? (
            <img src={resource.cover_image_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-4xl text-muted-foreground" aria-hidden="true">
              {resource.icon || '◻'}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">{resource.name}</h1>
            <Badge variant={RESOURCE_STATUS_VARIANT[status] ?? 'default'} size="sm">
              {t(`booking.resource.status.${RESOURCE_STATUS_KEYS[status]}`)}
            </Badge>
          </div>
          {resource.location?.display_name && <p className="mt-1 text-sm text-muted-foreground">{resource.location.display_name}</p>}
          <p className="mt-1 text-lg font-medium text-foreground">
            {resource.pricing?.base_price_cents !== undefined ? fmtMoney(resource.pricing.base_price_cents, resource.pricing.currency, locale) : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setBookOpen(true)} disabled={status !== 'BOOKABLE_RESOURCE_STATUS_ACTIVE'}>
            {t('booking.book.cta')}
          </Button>
          {isOwner && (
            <div className="relative">
              <button
                ref={menuTriggerRef}
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-expanded={menuOpen}
                aria-label={t('common.actions')}
                className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground hover:bg-muted"
              >
                ⋯
              </button>
              <AnchoredMenu
                anchor={menuTriggerRef}
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                align="end"
                ariaLabel={t('common.actions')}
                className="w-52 py-1"
              >
                <button type="button" onClick={() => { setMenuOpen(false); setEditOpen(true); }} className="block w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted">
                  {t('common.edit')}
                </button>
                <div className="px-3 py-1.5">
                  <Select value={status} onChange={(e) => { setMenuOpen(false); changeStatus(e.target.value); }} options={statusOptions} aria-label={t('booking.resource.field.status')} />
                </div>
                <button type="button" onClick={() => { setMenuOpen(false); setDeleteOpen(true); }} className="block w-full px-3 py-1.5 text-left text-sm text-destructive hover:bg-muted">
                  {t('common.delete')}
                </button>
              </AnchoredMenu>
            </div>
          )}
        </div>
      </div>

      {resource.description && <p className="text-sm text-muted-foreground">{resource.description}</p>}

      {resource.categories && resource.categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {resource.categories.map((c) => (
            <span key={c} className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {c}
            </span>
          ))}
        </div>
      )}

      {waitlistJoined !== null && (
        <Alert variant="info">{t('booking.waitlist.joinedPosition', { position: waitlistJoined })}</Alert>
      )}
      <button type="button" onClick={joinWaitlist} disabled={joiningWaitlist} className="text-sm text-primary hover:underline">
        {t('booking.waitlist.joinCta')}
      </button>

      <div role="tablist" aria-label={t('booking.resource.tabsLabel')} className="flex gap-1 border-b border-border">
        {(['overview', 'reviews', ...(isOwner ? (['waitlist', 'insights'] as Tab[]) : [])] as Tab[]).map((tb) => (
          <button
            key={tb}
            role="tab"
            aria-selected={tab === tb}
            onClick={() => setTab(tb)}
            className={`px-3 py-2 text-sm ${tab === tb ? 'border-b-2 border-primary font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {t(`booking.resource.tab.${tb}`)}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-2 text-sm text-foreground">
          <p>{t('booking.resource.field.capacity')}: {resource.capacity}</p>
          {resource.average_rating !== undefined && resource.average_rating > 0 && (
            <p>★ {resource.average_rating.toFixed(1)} ({resource.total_reviews ?? 0} {t('booking.review.reviewsCount')})</p>
          )}
        </div>
      )}

      {tab === 'reviews' && (
        <div className="space-y-3">
          {reviewsLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!reviewsLoading && reviewSummary && reviewSummary.total_reviews > 0 && (
            <p className="text-sm text-foreground">★ {reviewSummary.average_rating.toFixed(1)} · {reviewSummary.total_reviews} {t('booking.review.reviewsCount')}</p>
          )}
          {!reviewsLoading && reviews.length === 0 && <p className="text-sm text-muted-foreground">{t('booking.review.empty')}</p>}
          {reviews.map((r) => (
            <div key={r.entity?.id} className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{r.reviewer?.display_name || t('booking.review.anonymous')}</span>
                <span className="text-sm text-warning">{'★'.repeat(r.overall_rating)}</span>
              </div>
              {r.title && <p className="mt-1 text-sm font-medium text-foreground">{r.title}</p>}
              {r.comment && <p className="mt-0.5 text-sm text-muted-foreground">{r.comment}</p>}
              {r.response_text && (
                <p className="mt-2 rounded bg-muted p-2 text-xs text-muted-foreground">{t('booking.review.providerResponse')}: {r.response_text}</p>
              )}
              {isOwner && !r.response_text && (
                <ReviewResponseInline onSubmit={(text) => respond(r, text)} />
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'waitlist' && isOwner && (
        <div className="space-y-2">
          {waitlistLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!waitlistLoading && waitlist.length === 0 && <p className="text-sm text-muted-foreground">{t('booking.waitlist.empty')}</p>}
          {waitlist.map((w) => (
            <div key={w.entity?.id} className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
              <span>#{w.position} — {w.customer?.display_name || t('booking.review.anonymous')}</span>
              <Badge variant="default" size="sm">{t(`booking.waitlist.status.${WAITLIST_STATUS_KEYS[w.status ?? 'WAITLIST_STATUS_WAITING']}`)}</Badge>
            </div>
          ))}
        </div>
      )}

      {tab === 'insights' && isOwner && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {statsLoading && <p className="col-span-full text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!statsLoading && stats && (
            <>
              <StatTile label={t('booking.insights.totalBookings')} value={String(stats.total_bookings)} />
              <StatTile label={t('booking.insights.completed')} value={String(stats.completed_bookings)} />
              <StatTile label={t('booking.insights.cancelled')} value={String(stats.cancelled_bookings)} />
              <StatTile label={t('booking.insights.noShows')} value={String(stats.no_show_bookings)} />
              <StatTile label={t('booking.insights.revenue')} value={fmtMoney(stats.total_revenue_cents, stats.currency, locale)} />
              <StatTile label={t('booking.insights.occupancy')} value={`${Math.round(stats.occupancy_rate * 100)}%`} />
              <StatTile label={t('booking.insights.avgRating')} value={stats.average_rating.toFixed(1)} />
              <StatTile label={t('booking.insights.waitlist')} value={String(stats.waitlist_entries)} />
            </>
          )}
        </div>
      )}

      <BookDialog open={bookOpen} onClose={() => setBookOpen(false)} resource={resource} scope={scope} onBooked={load} />
      <ResourceFormDialog open={editOpen} onClose={() => setEditOpen(false)} resource={resource} bookingTypes={bookingTypes} scope={scope} onSaved={load} />
      <ConfirmDialog
        open={deleteOpen}
        title={t('booking.resource.deleteConfirmTitle')}
        description={t('booking.resource.deleteConfirmDescription')}
        destructive
        onCancel={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
});

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}

function ReviewResponseInline({ onSubmit }: { onSubmit: (text: string) => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-2 text-xs text-primary hover:underline">
        {t('booking.review.respond')}
      </button>
    );
  }
  return (
    <div className="mt-2 flex gap-2">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="input"
        placeholder={t('booking.review.responsePlaceholder')}
      />
      <Button
        size="sm"
        onClick={() => {
          if (text.trim()) onSubmit(text.trim());
          setOpen(false);
          setText('');
        }}
      >
        {t('booking.review.send')}
      </Button>
    </div>
  );
}
